'use client'

/**
 * React hooks for analytics
 */
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useRef } from 'react'

import { getTrafficSourceParams, trackEvent, trackPageView } from '.'
import type { UserContext } from './types'

/**
 * Hook for tracking page views
 *
 * @param pageName Name of the page
 * @param userContext Optional user context data
 */
export function usePageViewTracking(pageName: string, userContext?: UserContext) {
  const searchParams = useSearchParams()
  const tracked = useRef(false)

  useEffect(() => {
    // Only track once per mount
    if (tracked.current) {
      return
    }
    tracked.current = true

    // Combine user context with traffic source data
    const sourceParams = getTrafficSourceParams(searchParams)

    trackPageView(pageName, {
      ...userContext,
      ...sourceParams,
    })
  }, [pageName, searchParams, userContext])
}

/**
 * Hook that provides tracking functions with user context pre-filled
 *
 * @param userContext User context data
 */
export function useAnalytics(userContext?: UserContext) {
  const searchParams = useSearchParams()
  const sourceParams = getTrafficSourceParams(searchParams)

  const track = useCallback(
    (eventName: string, properties = {}) => {
      trackEvent(eventName, {
        ...userContext,
        ...sourceParams,
        ...properties,
      })
    },
    [userContext, sourceParams],
  )

  return {
    track,
  }
}

/**
 * Hook to track form abandonment
 *
 * @param formState Object containing form state
 * @param options Options for tracking
 */
export function useFormAbandonmentTracking(
  formState: { started: boolean; completed: boolean; step?: string | number },
  options: {
    eventName: string
    additionalData: object
  },
) {
  const { track } = useAnalytics()

  useEffect(() => {
    // Only add listener if form has been started but not completed
    if (!formState.started || formState.completed) {
      return
    }

    const handleBeforeUnload = () => {
      track(options.eventName, {
        step: formState.step,
        ...options.additionalData,
      })
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [formState, track, options])
}
